import React from 'react';
import { Icon } from '@fluentui/react';
import { ILayout, ILayoutColumn, ILayoutElement, ILayoutRow, LayoutAlign, LayoutVAlign } from '../layout';
import { IHeaderLink } from '../headerLinksConfig';

// AC-951 / S3.3 — an admin-side, approximate live preview of a header/footer `layout`.
//
// The real renderer (ac-storefront-web LayoutRenderer) lives in the storefront app and can't be
// imported here, so this mirrors its grid mapping — rows are CSS grids whose tracks come from
// `column.width`, columns are flex rows honouring align/vAlign — and draws a lightweight placeholder
// for each element type. It's a layout/spacing sanity check, not a pixel-exact render: per-element
// styles and real data (products, cart count) aren't reproduced.

interface IProps {
    layout: ILayout;
    region: 'header' | 'footer';
    logo?: string;
    title?: string;
    barColor?: string;
    barTextColor?: string;
    mobile?: boolean;
}

const justify = (a?: LayoutAlign): React.CSSProperties['justifyContent'] =>
    a === 'left' ? 'flex-start' : a === 'right' ? 'flex-end' : a === 'center' ? 'center'
        : a === 'between' ? 'space-between' : a === 'around' ? 'space-around' : undefined;
const alignItems = (v?: LayoutVAlign): React.CSSProperties['alignItems'] =>
    v === 'top' ? 'flex-start' : v === 'bottom' ? 'flex-end' : v === 'center' ? 'center' : undefined;
const track = (w?: number | string): string => (w == null ? 'auto' : typeof w === 'number' ? `${w}px` : String(w));
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

// A small pill used to stand in for an element that has no meaningful visual of its own.
const Chip: React.FC<{ icon?: string; label: string; color?: string }> = ({ icon, label, color }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 14, border: `1px solid ${color ?? 'currentColor'}`, opacity: 0.85, fontSize: 12, whiteSpace: 'nowrap' }}>
        {icon && <Icon iconName={icon} styles={{ root: { fontSize: 12 } }} />}
        {label}
    </span>
);

const renderElement = (el: ILayoutElement, ctx: IProps): React.ReactNode => {
    const props = (el.props ?? {}) as Record<string, unknown>;
    switch (el.type) {
        case 'logo':
            return ctx.logo
                ? <img src={ctx.logo} alt='Logo' style={{ height: '2.2em', width: 'auto' }} />
                : <Chip icon='Photo2' label='Logo' color={ctx.barTextColor} />;
        case 'title':
            return <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{ctx.title || 'Store title'}</span>;
        case 'text':
            return <span>{str(props.text) || 'Text'}</span>;
        case 'image':
            return str(props.url)
                ? <img src={str(props.url)} alt={str(props.alt)} style={{ maxHeight: '2.4em', width: 'auto' }} />
                : <Chip icon='FileImage' label='Image' color={ctx.barTextColor} />;
        case 'divider':
            return str(props.orientation) === 'horizontal'
                ? <span style={{ display: 'block', width: '100%', borderTop: '1px solid currentColor', opacity: 0.3 }} />
                : <span style={{ alignSelf: 'stretch', borderLeft: '1px solid currentColor', opacity: 0.3 }} />;
        case 'spacer':
            return <span style={{ flex: '1 1 auto' }} />;
        case 'html':
            return <Chip icon='Code' label='Custom HTML' color={ctx.barTextColor} />;
        case 'search':
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 160, padding: '4px 10px', borderRadius: 16, border: `1px solid ${ctx.barTextColor ?? 'currentColor'}`, opacity: 0.7, fontSize: 12 }}>
                    <Icon iconName='Search' styles={{ root: { fontSize: 12 } }} /> Search…
                </span>
            );
        case 'cart':
            return <Chip icon='ShoppingCart' label='Cart' color={ctx.barTextColor} />;
        case 'account':
            return <Chip icon='Contact' label='Account' color={ctx.barTextColor} />;
        case 'social':
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {['FacebookLogo', 'Twitter', 'LinkedInLogo', 'YouTubeLogo'].map((n) => <Icon key={n} iconName={n} styles={{ root: { fontSize: 16, opacity: 0.85 } }} />)}
                </span>
            );
        case 'contact':
            return (
                <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: 12, opacity: 0.85 }}>
                    <span>contact@example.com</span>
                    <span>(555) 555-0100</span>
                </span>
            );
        case 'link':
            return <span style={{ textDecoration: 'underline' }}>{(props as IHeaderLink).label || 'Link'}</span>;
        case 'menu': {
            const label = (props as IHeaderLink).label || 'Menu';
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    {label}<Icon iconName='ChevronDownSmall' styles={{ root: { fontSize: 10 } }} />
                </span>
            );
        }
        case 'linkGroup': {
            const links = (props.links as IHeaderLink[] | undefined) ?? [];
            if (links.length === 0) return <Chip icon='BulletedList2' label='Link group' color={ctx.barTextColor} />;
            return (
                <span style={{ display: 'inline-flex', gap: 16, flexWrap: 'wrap' }}>
                    {links.map((l, i) => <span key={l.id ?? i} style={{ textDecoration: 'underline' }}>{l.label || 'Link'}</span>)}
                </span>
            );
        }
        default:
            return <Chip label={el.type} color={ctx.barTextColor} />;
    }
};

const Column: React.FC<{ col: ILayoutColumn; ctx: IProps }> = ({ col, ctx }) => (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: alignItems(col.vAlign) ?? 'center', justifyContent: justify(col.align) ?? 'flex-start', gap: col.gap ?? 8, minWidth: 0 }}>
        {(col.elements ?? []).map((el, i) => <React.Fragment key={el.id ?? i}>{renderElement(el, ctx)}</React.Fragment>)}
    </div>
);

const Row: React.FC<{ row: ILayoutRow; ctx: IProps; stack: boolean }> = ({ row, ctx, stack }) => {
    // Stack mode (mobile auto-stack) drops hideOnMobile columns and collapses to a single column,
    // mirroring the runtime Row.
    const cols = (row.columns ?? []).filter((c) => !(stack && c.hideOnMobile));
    const gridTemplateColumns = stack ? '1fr' : cols.map((c) => track(c.width)).join(' ') || '1fr';
    return (
        <div style={{ display: 'grid', gridTemplateColumns, gap: row.gap ?? 12, alignItems: alignItems(row.vAlign) ?? 'center', justifyContent: justify(row.align) }}>
            {cols.map((col, i) => <Column key={i} col={col} ctx={ctx} />)}
        </div>
    );
};

const LayoutPreview: React.FC<IProps> = (props) => {
    const { layout, region, barColor, barTextColor, mobile } = props;
    // Match the runtime resolution: mobile + custom strategy -> the authored mobile rows (own tracks);
    // mobile + stack (default) -> desktop rows, auto-stacked; desktop -> desktop rows as authored.
    const useCustomMobile = !!mobile && layout.mobile?.strategy === 'custom' && (layout.mobile.rows?.length ?? 0) > 0;
    const rows = useCustomMobile ? (layout.mobile?.rows ?? []) : (layout.rows ?? []);
    const stack = !!mobile && !useCustomMobile;
    const frame: React.CSSProperties = mobile ? { maxWidth: 390, margin: '0 auto' } : {};
    return (
        <div style={{ ...frame, background: barColor ?? (region === 'footer' ? '#0e1c30' : '#ffffff'), color: barTextColor ?? (region === 'footer' ? '#ffffff' : '#111111'), padding: '10px 16px', borderRadius: 4, border: '1px solid #e1dfdd' }}>
            <div style={{ maxWidth: mobile ? undefined : layout.maxWidth, margin: (!mobile && layout.maxWidth) ? '0 auto' : undefined, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((row, i) => <Row key={i} row={row} ctx={props} stack={stack} />)}
            </div>
        </div>
    );
};

export default LayoutPreview;
